import {useEffect, useRef, useState} from 'react';
import {useCalendarState, useRangeCalendarState} from 'react-stately';
import {
    useButton,
    useCalendar,
    useCalendarCell,
    useCalendarGrid,
    useRangeCalendar,
    useLocale,
} from 'react-aria';
import {
    createCalendar,
    isSameMonth,
    getWeeksInMonth,
    endOfMonth,
    CalendarDate,
    getDayOfWeek,
} from '@internationalized/date';
import {ChevronLeftIcon, ChevronRightIcon} from '@chakra-ui/icons';
import {
    Box,
    Button,
    ButtonGroup,
    Grid,
    GridItem,
    Heading,
    Stack,
    Text,
} from '@chakra-ui/react';
import {useTranslation} from 'react-i18next';
import {useColorModeValueKey} from 'hooks/useColors';
import {addYears, format, setYear, subYears} from 'date-fns';

const CalendarButton = (props) => {
    const ref = useRef();
    const {buttonProps} = useButton(props, ref);
    return (
        <Button {...buttonProps} ref={ref} size="sm">
            {props.children}
        </Button>
    );
};

const CalendarCell = ({state, date, currentMonth}) => {
    const ref = useRef();
    const {buttonProps, isSelected, isInvalid, formattedDate} =
        useCalendarCell({date}, state, ref);

    const isOutsideMonth = !isSameMonth(currentMonth, date);
    return (
        <Box
            textAlign="center"
            display={'flex'}
            alignItems="center"
            justifyContent="center"
            hidden={isOutsideMonth}
        >
            <Button
                {...buttonProps}
                ref={ref}
                size="xs"
                colorScheme={isInvalid ? 'red' : 'gray'}
                variant={isSelected ? 'primary' : 'ghost'}
                fontWeight={'normal'}
                fontSize="xs"
                color={useColorModeValueKey('text')}
                w={8}
                h={8}
            >
                {formattedDate}
            </Button>
        </Box>
    );
};

export function CalendarGrid({state, offset = {}}) {
    const {locale} = useLocale();
    // console.log(state.visibleRange);
    const startDate = state.visibleRange.start.add(offset);
    const endDate = endOfMonth(startDate);
    const {gridProps, headerProps, weekDays} = useCalendarGrid(
        {
            startDate,
            endDate,
        },
        state,
    );

    // Get the number of weeks in the month so we can render the proper number of rows.
    // Use startDate (with offset) instead of state.visibleRange.start
    // Force Sunday as the first day of the week regardless of locale.
    const weeksInMonth = getWeeksInMonth(startDate, locale, 'sun');
    const daysInMonth = startDate.calendar.getDaysInMonth(startDate);

    // Get the day of week for the first day of the month, always relative to Sunday.
    const firstDayOfWeek = getDayOfWeek(startDate, locale, 'sun');

    // The weekday headers returned by useCalendarGrid follow the locale's week start.
    // Rotate them so Sunday is always shown first.
    const weekStart = getDayOfWeek(new CalendarDate(2024, 1, 7), locale);
    const rotateBy = (7 - weekStart) % 7;
    const rotatedWeekDays = rotateBy
        ? [...weekDays.slice(-rotateBy), ...weekDays.slice(0, -rotateBy)]
        : weekDays;

    // Generate array with all days in month plus leading/trailing empty slots
    const totalCells = weeksInMonth * 7;
    const daysArray = [];

    // Add empty slots for days before the first day of month
    for (let i = 0; i < firstDayOfWeek; i++) {
        daysArray.push(null);
    }

    // Add all days in the month using new CalendarDate
    for (let day = 1; day <= daysInMonth; day++) {
        daysArray.push(new CalendarDate(startDate.year, startDate.month, day));
    }

    // Fill remaining slots with null
    while (daysArray.length < totalCells) {
        daysArray.push(null);
    }

    return (
        <Box>
            <Grid templateColumns="repeat(7, 1fr)" gap={0} {...gridProps}>
                {/* Week day headers */}
                <Box {...headerProps}>
                    <Grid templateColumns="repeat(7, 1fr)" gap={0}>
                        {rotatedWeekDays.map((day, index) => (
                            <Box
                                key={index}
                                textAlign="center"
                            >
                                <Text fontSize="xs">{day}</Text>
                            </Box>
                        ))}
                    </Grid>
                </Box>

                {/* Day cells */}
                {daysArray.map((date, index) =>
                    date ? (
                        <CalendarCell
                            key={index}
                            state={state}
                            date={date}
                            currentMonth={startDate}
                        />
                    ) : (
                        <Box key={index} />
                    ),
                )}
            </Grid>
        </Box>
    );
}

function getYearsBetween() {
    const thisYear = new Date().getFullYear();
    const startYear = thisYear - 70;
    const endYear = thisYear + 9;
    const years = [];

    for (let i = endYear; i >= startYear; i--) {
        years.push(i);
    }

    return years;
}

export function CalenderYearGrid(props) {
    const {value, state, setYearMode, setMonthMode} = props;
    const years = getYearsBetween();
    const stackRef = useRef(null);
    // console.log(props, value);
    return (
        <Stack h={64} overflow={'scroll'} ref={stackRef}>
            <Grid templateColumns="repeat(4, 1fr)" gap={2}>
                {years.map((year, i) => {
                    const isSelected = value?.year === year;
                    return (
                        <GridItem w="100%" key={year}>
                            <Button
                                size={{base: 'xs', md: 'sm'}}
                                variant={isSelected ? 'primary' : 'ghost'}
                                width="100%"
                                fontWeight={'normal'}
                                color={useColorModeValueKey('text')}
                                onClick={() => {
                                    state?.selectDate(
                                        new CalendarDate(
                                            year,
                                            value?.month || 1,
                                            1,
                                        ),
                                    );
                                    setMonthMode(true);
                                    setYearMode(false);
                                }}
                            >
                                {year}
                            </Button>
                        </GridItem>
                    );
                })}
            </Grid>
        </Stack>
    );
}
export function CalenderMonthGrid(props) {
    const {value, state, setMonthMode, setYearMode} = props;
    const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    // console.log(state)
    return (
        <Stack h={'160px'} overflow={'scroll'}>
            <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                {months.map((month, i) => {
                    const isSelected = value?.month === month;
                    return (
                        <GridItem w="100%" key={month}>
                            <Button
                                size={{base: 'xs', md: 'sm'}}
                                variant={isSelected ? 'primary' : 'ghost'}
                                width="100%"
                                fontWeight={'normal'}
                                color={useColorModeValueKey('text')}
                                onClick={() => {
                                    state?.selectDate(
                                        new CalendarDate(
                                            value?.year,
                                            month,
                                            1,
                                        ),
                                    );
                                    state?.setFocusedDate(
                                        new CalendarDate(
                                            value?.year,
                                            month,
                                            1,
                                        ),
                                    );
                                    setMonthMode(false);
                                    setYearMode(false);
                                }}
                            >
                                {format(new Date(`1970-${month}-1`), 'MMMM')}
                            </Button>
                        </GridItem>
                    );
                })}
            </Grid>
        </Stack>
    );
}

const Calendar = (props) => {
    const {isOpen} = props;
    const {i18n} = useTranslation('app');
    const state = useCalendarState({
        ...props,
        locale: i18n.language,
        createCalendar,
    });

    const ref = useRef();
    const {calendarProps, prevButtonProps, nextButtonProps, title} =
        useCalendar(props, state, ref);
    const [yearMode, setYearMode] = useState(false);
    const [monthMode, setMonthMode] = useState(false);
    return (
        <div {...calendarProps} ref={ref}>
            {props.yearMode ? (
                <CalenderYearGrid
                    state={state}
                    {...props}
                    setYearMode={setYearMode}
                    setMonthMode={setMonthMode}
                />
            ) : monthMode ? (
                <CalenderMonthGrid
                    state={state}
                    {...props}
                    setYearMode={setYearMode}
                    setMonthMode={setMonthMode}
                />
            ) : (
                <>
                    <Box display="flex" alignItems="center" paddingBottom="4">
                        <CalendarButton {...prevButtonProps}>
                            <ChevronLeftIcon w={6} h={6} />
                        </CalendarButton>
                        <Button
                            size="sm"
                            flex={1}
                            onClick={() => setYearMode(true)}
                        >
                            <Heading size="sm" flex="1" textAlign="center">
                                {title}
                            </Heading>
                        </Button>
                        <CalendarButton {...nextButtonProps}>
                            <ChevronRightIcon w={6} h={6} />
                        </CalendarButton>
                    </Box>
                    <CalendarGrid state={state} />
                </>
            )}
        </div>
    );
};

export default Calendar;

export const RangeCalendar = (props) => {
    const {i18n} = useTranslation('app');
    const state = useRangeCalendarState({
        ...props,
        locale: i18n.language,
        createCalendar,
        visibleDuration: {months: 2},
    });

    const ref = useRef();
    const {calendarProps, prevButtonProps, nextButtonProps, title} =
        useRangeCalendar(props, state, ref);
    const [yearMode, setYearMode] = useState(false);
    const [monthMode, setMonthMode] = useState(false);

    // Use focusedDate as value for year/month selection
    const value = state.focusedDate;

    return (
        <div {...calendarProps} ref={ref}>
            {yearMode ? (
                <CalenderYearGrid
                    state={state}
                    value={value}
                    setYearMode={setYearMode}
                    setMonthMode={setMonthMode}
                />
            ) : monthMode ? (
                <CalenderMonthGrid
                    state={state}
                    value={value}
                    setYearMode={setYearMode}
                    setMonthMode={setMonthMode}
                />
            ) : (
                <>
                    <Box display="flex" alignItems="center" paddingBottom="4">
                        <CalendarButton {...prevButtonProps}>
                            <ChevronLeftIcon w={6} h={6} />
                        </CalendarButton>
                        <Button
                            size="sm"
                            flex={1}
                            onClick={() => setYearMode(true)}
                        >
                            <Heading
                                size={{base: 'xs', md: 'sm'}}
                                flex="1"
                                textAlign="center"
                            >
                                {title}
                            </Heading>
                        </Button>
                        <CalendarButton {...nextButtonProps}>
                            <ChevronRightIcon w={6} h={6} />
                        </CalendarButton>
                    </Box>
                    <Stack
                        direction={{base: 'column', md: 'row'}}
                        display="flex"
                        gap={2}
                    >
                        <Box flex={1} minW={0}>
                            <CalendarGrid state={state} />
                        </Box>
                        <Box flex={1} minW={0}>
                            <CalendarGrid state={state} offset={{months: 1}} />
                        </Box>
                    </Stack>
                </>
            )}
        </div>
    );
};
